/**
 * Centralized Logging Utility
 * Provides environment-aware console logging with structured context support
 * 
 * Features:
 * - Module-based logging with consistent formatting
 * - Environment-aware log levels
 * - Request ID correlation support
 */

// ============================================================================
// Types & Enums
// ============================================================================

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export interface LogContext {
  [key: string]: any;
  requestId?: string;
  userId?: string;
  tenantId?: string;
}

interface LogConfig {
  level: LogLevel;
  enableConsole: boolean;
}

// ============================================================================
// Logger Class
// ============================================================================

class Logger {
  private module: string;
  private config: LogConfig;

  constructor(module: string, config?: Partial<LogConfig>) {
    this.module = module;
    this.config = {
      level: this.getDefaultLogLevel(),
      enableConsole: true,
      ...config,
    };
  }

  /**
   * Determine default log level based on environment
   */
  private getDefaultLogLevel(): LogLevel {
    const env = process.env.NODE_ENV;
    const apiEnv = process.env.REACT_APP_API_ENV;
    
    // Production: only INFO and above
    if (env === 'production' || apiEnv === 'prd') {
      return LogLevel.INFO;
    }
    
    // Development/local: all logs
    return LogLevel.DEBUG;
  }

  /**
   * Check if log should be emitted based on level
   */
  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.level;
  }

  /**
   * Format log message with timestamp and module prefix
   */
  private formatMessage(level: string, message: string, operation?: string): string {
    const operationPrefix = operation ? `[${operation}]` : '';
    return `[${this.module}]${operationPrefix} [${level}] ${message}`;
  }

  /**
   * Log debug message (development only)
   */
  debug(message: string, context?: LogContext): void;
  debug(operation: string, message: string, context?: LogContext): void;
  debug(operationOrMessage: string, messageOrContext?: string | LogContext, maybeContext?: LogContext): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    
    const [operation, message, context] = this.parseLogArgs(operationOrMessage, messageOrContext, maybeContext);
    
    if (this.config.enableConsole) {
      console.debug(this.formatMessage('DEBUG', message, operation), context || '');
    }
  }

  /**
   * Log info message
   */
  info(message: string, context?: LogContext): void;
  info(operation: string, message: string, context?: LogContext): void;
  info(operationOrMessage: string, messageOrContext?: string | LogContext, maybeContext?: LogContext): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    
    const [operation, message, context] = this.parseLogArgs(operationOrMessage, messageOrContext, maybeContext);
    
    if (this.config.enableConsole) {
      console.info(this.formatMessage('INFO', message, operation), context || '');
    }
  }

  /**
   * Log warning message
   */
  warn(message: string, context?: LogContext): void;
  warn(operation: string, message: string, context?: LogContext): void;
  warn(operationOrMessage: string, messageOrContext?: string | LogContext, maybeContext?: LogContext): void {
    if (!this.shouldLog(LogLevel.WARN)) return;
    
    const [operation, message, context] = this.parseLogArgs(operationOrMessage, messageOrContext, maybeContext);
    
    if (this.config.enableConsole) {
      console.warn(this.formatMessage('WARN', message, operation), context || '');
    }
  }

  /**
   * Log error message
   */
  error(message: string, context?: LogContext): void;
  error(operation: string, message: string, context?: LogContext): void;
  error(operationOrMessage: string, messageOrContext?: string | LogContext, maybeContext?: LogContext): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;
    
    const [operation, message, context] = this.parseLogArgs(operationOrMessage, messageOrContext, maybeContext);
    
    if (this.config.enableConsole) {
      console.error(this.formatMessage('ERROR', message, operation), context || '');
    }
  }

  /**
   * Parse overloaded log method arguments
   */
  private parseLogArgs(
    arg1: string,
    arg2?: string | LogContext,
    arg3?: LogContext
  ): [string | undefined, string, LogContext | undefined] {
    // If arg2 is a string, then arg1 is operation, arg2 is message, arg3 is context
    if (typeof arg2 === 'string') {
      return [arg1, arg2, arg3];
    }
    // Otherwise arg1 is message, arg2 is context
    return [undefined, arg1, arg2];
  }

  /**
   * Log user access (section/application navigation)
   * Logs to console and K8s will automatically collect and send to Splunk
   */
  accessLog(
    application: string,
    userAliasName: string,
    tenantId: string,
    additionalContext?: LogContext
  ): void {
    const context: LogContext = {
      application,
      useraliasname: userAliasName,
      tenant: tenantId,
      ...additionalContext,
    };
    
    this.info("User's application access from NextGen UI", context);
  }
}

// ============================================================================
// Factory & Exports
// ============================================================================

/**
 * Create a logger instance for a specific module
 * 
 * @example
 * const logger = createLogger('API');
 * logger.info('Request started', { url: '/api/users' });
 * logger.error('Request failed', { error: err.message });
 */
export function createLogger(module: string, config?: Partial<LogConfig>): Logger {
  return new Logger(module, config);
}
